import { FormControl, FormLabel, Switch } from '@chakra-ui/react';
import { CHART_EDITED, Mixpanel } from 'libs/third-party/Mixpanel';
import { ChartSettingsEntry, Labels } from 'modules/Editor/components/SideMenuSettings/ChartSettings/ChartOptions';
import { SideSettingDivider } from 'modules/Editor/components/SideMenuSettings/SidemenuSettingsDivider';
import { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetType } from 'types/widget.types';
import { AllWidgetData } from 'widgets/Widget.types';
import { useWidget } from 'widgets/sdk';

import { DEFAULT_TEXT_STYLE, DEFAULT_Y2_Label_TOGGLE, HAS_CHART_TYPE_PROPS } from '../../ChartWidget.config';
import { getChartInfo } from '../../ChartWidget.helpers';
import { ChartWidgetData } from '../../ChartWidget.types';
import { FontSettings, TextSettings } from '../TextStyleSettings';

interface LabelSettingProps {
  widgetType: WidgetType;
  showY2Datalabels?: boolean;
  showY1Datalabels?: boolean;
}

export const LabelSetting = ({
  showY1Datalabels = true,
  showY2Datalabels,
  widgetType,
}: Readonly<LabelSettingProps>) => {
  const { t } = useTranslation('chart_widget', { useSuspense: false });
  const defaultY1Label = showY2Datalabels ? t('labels.y1DataLabel') : t('labels.dataLabel');

  return (
    <Labels>
      <XAxisLabel widgetType={widgetType} />
      <XAxisSubSetting />
      <SideSettingDivider />
      <YAxisLabel widgetType={widgetType} />
      <YAxisSubSetting />
      {showY1Datalabels && (
        <>
          <SideSettingDivider />
          <DataLabel label={defaultY1Label} widgetType={widgetType} />
          <DataLabelSubSetting />
        </>
      )}
      {showY2Datalabels && (
        <>
          <SideSettingDivider />
          <DataLabel label={t('labels.y2DataLabel')} showY2Datalabels widgetType={widgetType} />
          <DataLabelSubSetting showY2Datalabels />
        </>
      )}
    </Labels>
  );
};

export const XAxisSubSetting = () => {
  const {
    xAxis: { enablelabels: enableLabels = true },
  } = useWidget<ChartWidgetData>();

  if (!enableLabels) return null;

  return (
    <>
      <XAxisLabelFont />
      <XAxisLabelText />
    </>
  );
};

export const YAxisSubSetting = () => {
  const {
    yAxis: { enablelabels: enableLabels = true },
  } = useWidget<ChartWidgetData>();

  if (!enableLabels) return null;

  return (
    <>
      <YAxisLabelFont />
      <YAxisLabelText />
    </>
  );
};

export const DataLabelSubSetting = ({ showY2Datalabels = false }) => {
  const {
    dataLabels: { enabled: enableLabels },
    y2DataLabels: { enabled: enableY2Labels = DEFAULT_Y2_Label_TOGGLE } = {},
  } = useWidget<ChartWidgetData>();
  const isEnabled = showY2Datalabels ? enableY2Labels : enableLabels;

  if (!isEnabled) return null;

  return (
    <>
      <DataLabelFont showY2Datalabels={showY2Datalabels} />
      <DataLabelText showFontColor showY2Datalabels={showY2Datalabels} />
    </>
  );
};

export const LabelSettingWithoutAxis = ({ widgetType }: Readonly<LabelSettingProps>) => {
  const {
    dataLabels: { enabled: enableLabels },
    chartType,
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onShowDataLabelChange = () => {
    updateWidget({ dataLabels: { enabled: !enableLabels } });
    let widgetData: AllWidgetData | undefined;
    if (HAS_CHART_TYPE_PROPS.includes(widgetType)) {
      widgetData = { chartType } as ChartWidgetData;
    }
    Mixpanel.track(CHART_EDITED, {
      styling_property: CHART_EDITED.styling_property.Labels,
      styling_value: !enableLabels ? CHART_EDITED.styling_value.On : CHART_EDITED.styling_value.Off,
      chart_type: getChartInfo(widgetType, widgetData).widgetTypeName,
    });
  };

  return (
    <Labels isCheck={enableLabels} onToggle={onShowDataLabelChange}>
      <DataLabelFont />
      <DataLabelText showFontColor />
    </Labels>
  );
};

export const XAxisLabel = ({ widgetType }: Readonly<LabelSettingProps>) => {
  const { t } = useTranslation('chart_widget', { useSuspense: false });
  const {
    xAxis: { enablelabels = true },
    chartType,
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const label = t('labels.xAxisLabel');

  const onShowXAxisLabelChange = () => {
    updateWidget({ xAxis: { enablelabels: !enablelabels } });
    let widgetData: AllWidgetData | undefined;
    if (HAS_CHART_TYPE_PROPS.includes(widgetType)) {
      widgetData = { chartType } as ChartWidgetData;
    }

    Mixpanel.track(CHART_EDITED, {
      styling_property: CHART_EDITED.styling_property.Labels,
      styling_value: !enablelabels
        ? CHART_EDITED.styling_value.XAxisLabelsOn
        : CHART_EDITED.styling_value.XAxisLabelsOff,
      chart_type: getChartInfo(widgetType, widgetData).widgetTypeName,
    });
  };

  return (
    <ChartSettingsEntry
      label={label}
      containerProps={{
        as: FormControl,
      }}
      labelProps={{
        as: FormLabel,
      }}
    >
      <Switch onChange={onShowXAxisLabelChange} isChecked={enablelabels} />
    </ChartSettingsEntry>
  );
};

export const XAxisLabelFont = () => {
  const {
    xAxis: {
      labelStyle: { fontFamily, fontSize },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onFontFamilyChange = useCallback(
    (fontFamily: string) => {
      updateWidget({
        xAxis: { labelStyle: { fontFamily } },
      });
    },
    [updateWidget],
  );

  const onFontSizeChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      updateWidget({ xAxis: { labelStyle: { fontSize: `${event.target.value}px` } } });
    },
    [updateWidget],
  );

  return (
    <FontSettings
      fontFamily={fontFamily}
      fontSize={fontSize}
      onChangeFontFamily={onFontFamilyChange}
      onChangeFontSize={onFontSizeChange}
    />
  );
};

export const XAxisLabelText = () => {
  const {
    xAxis: {
      labelStyle: { color, fontStyle, fontWeight, fontFamily },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onFontColorChange = useCallback(
    (color: string) => {
      updateWidget({
        xAxis: { labelStyle: { color } },
      });
    },
    [updateWidget],
  );

  const onFontWeightChange = useCallback(
    (fontWeight: string) => {
      updateWidget({ xAxis: { labelStyle: { fontWeight } } });
    },
    [updateWidget],
  );

  const onFontStyleChange = useCallback(
    (fontStyle: string) => {
      updateWidget({ xAxis: { labelStyle: { fontStyle } } });
    },
    [updateWidget],
  );

  return (
    <TextSettings
      fontColor={color}
      fontWeight={fontWeight}
      fontStyle={fontStyle}
      fontFamily={fontFamily}
      onFontColorChange={onFontColorChange}
      onFontWeightChange={onFontWeightChange}
      onFontStyleChange={onFontStyleChange}
    />
  );
};

export const YAxisLabel = ({ widgetType }: Readonly<LabelSettingProps>) => {
  const { t } = useTranslation('chart_widget', { useSuspense: false });
  const {
    yAxis: { enablelabels = true },
    chartType,
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const label = t('labels.yAxisLabel');

  const onShowYAxisLabelChange = () => {
    updateWidget({ yAxis: { enablelabels: !enablelabels } });
    let widgetData: AllWidgetData | undefined;
    if (HAS_CHART_TYPE_PROPS.includes(widgetType)) {
      widgetData = { chartType } as ChartWidgetData;
    }
    Mixpanel.track(CHART_EDITED, {
      styling_property: CHART_EDITED.styling_property.Labels,
      styling_value: !enablelabels
        ? CHART_EDITED.styling_value.YAxisLabelsOn
        : CHART_EDITED.styling_value.YAxisLabelsOff,
      chart_type: getChartInfo(widgetType, widgetData).widgetTypeName,
    });
  };

  return (
    <ChartSettingsEntry
      label={label}
      containerProps={{
        as: FormControl,
      }}
      labelProps={{
        as: FormLabel,
      }}
    >
      <Switch onChange={onShowYAxisLabelChange} isChecked={enablelabels} />
    </ChartSettingsEntry>
  );
};

export const YAxisLabelFont = () => {
  const {
    yAxis: {
      labelStyle: { fontFamily, fontSize },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onFontFamilyChange = useCallback(
    (fontFamily: string) => {
      updateWidget({
        yAxis: { labelStyle: { fontFamily } },
      });
    },
    [updateWidget],
  );

  const onFontSizeChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      updateWidget({ yAxis: { labelStyle: { fontSize: `${event.target.value}px` } } });
    },
    [updateWidget],
  );

  return (
    <FontSettings
      fontFamily={fontFamily}
      fontSize={fontSize}
      onChangeFontFamily={onFontFamilyChange}
      onChangeFontSize={onFontSizeChange}
    />
  );
};

export const YAxisLabelText = () => {
  const {
    yAxis: {
      labelStyle: { color, fontStyle, fontWeight, fontFamily },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onFontColorChange = useCallback(
    (color: string) => {
      updateWidget({
        yAxis: { labelStyle: { color } },
      });
    },
    [updateWidget],
  );

  const onFontWeightChange = useCallback(
    (fontWeight: string) => {
      updateWidget({ yAxis: { labelStyle: { fontWeight } } });
    },
    [updateWidget],
  );

  const onFontStyleChange = useCallback(
    (fontStyle: string) => {
      updateWidget({ yAxis: { labelStyle: { fontStyle } } });
    },
    [updateWidget],
  );

  return (
    <TextSettings
      fontFamily={fontFamily}
      fontColor={color}
      fontWeight={fontWeight}
      fontStyle={fontStyle}
      onFontColorChange={onFontColorChange}
      onFontWeightChange={onFontWeightChange}
      onFontStyleChange={onFontStyleChange}
    />
  );
};

interface DataLabelProps extends LabelSettingProps {
  label: string;
}

export const DataLabel = ({ label, showY2Datalabels, widgetType }: DataLabelProps) => {
  const {
    dataLabels: { enabled: enableLabels },
    y2DataLabels: { enabled: enableY2Labels = DEFAULT_Y2_Label_TOGGLE } = {},
    chartType,
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const isEnabled = showY2Datalabels ? enableY2Labels : enableLabels;

  const onShowDataLabelChange = () => {
    const propertyName = showY2Datalabels ? 'y2DataLabels' : 'dataLabels';
    updateWidget({ [propertyName]: { enabled: !isEnabled } });
    let widgetData: AllWidgetData | undefined;
    if (HAS_CHART_TYPE_PROPS.includes(widgetType)) {
      widgetData = { chartType } as ChartWidgetData;
    }

    Mixpanel.track(CHART_EDITED, {
      styling_property: CHART_EDITED.styling_property.Labels,
      styling_value: !isEnabled ? CHART_EDITED.styling_value.DataLabelsOn : CHART_EDITED.styling_value.DataLabelsOff,
      chart_type: getChartInfo(widgetType, widgetData).widgetTypeName,
    });
  };

  return (
    <ChartSettingsEntry
      label={label}
      containerProps={{
        as: FormControl,
      }}
      labelProps={{
        as: FormLabel,
      }}
    >
      <Switch onChange={onShowDataLabelChange} isChecked={isEnabled} />
    </ChartSettingsEntry>
  );
};

export const DataLabelFont = ({ showY2Datalabels = false }) => {
  const {
    dataLabels: {
      style: { fontFamily: y1FontFamily, fontSize: y1FontSize },
    },
    y2DataLabels: {
      style: {
        fontFamily: y2FontFamily = DEFAULT_TEXT_STYLE.fontFamily,
        fontSize: y2FontSize = DEFAULT_TEXT_STYLE.fontSize,
      } = {},
    } = {},
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const fontFamily = showY2Datalabels ? y2FontFamily : y1FontFamily;
  const fontSize = showY2Datalabels ? y2FontSize : y1FontSize;

  const labelPropertyname = showY2Datalabels ? 'y2DataLabels' : 'dataLabels';

  const onFontFamilyChange = useCallback(
    (fontFamily: string) => {
      updateWidget({
        [labelPropertyname]: { style: { fontFamily } },
      });
    },
    [labelPropertyname, updateWidget],
  );

  const onFontSizeChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      updateWidget({
        [labelPropertyname]: { style: { fontSize: `${event.target.value}px` } },
      });
    },
    [labelPropertyname, updateWidget],
  );

  return (
    <FontSettings
      fontFamily={fontFamily}
      fontSize={fontSize}
      onChangeFontFamily={onFontFamilyChange}
      onChangeFontSize={onFontSizeChange}
    />
  );
};

export const DataLabelText = ({ showFontColor = false, showY2Datalabels = false }) => {
  const {
    dataLabels: {
      style: { color: y1Color, fontStyle: y1FontStyle, fontWeight: y1FontWeight, fontFamily: y1FontFamily },
    },
    y2DataLabels: {
      style: {
        color: y2Color = DEFAULT_TEXT_STYLE.color,
        fontStyle: y2FontStyle = DEFAULT_TEXT_STYLE.fontStyle,
        fontWeight: y2FontWeight = DEFAULT_TEXT_STYLE.fontWeight,
        fontFamily: y2FontFamily = DEFAULT_TEXT_STYLE.fontFamily,
      } = {},
    } = {},
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const labelPropertyName = showY2Datalabels ? 'y2DataLabels' : 'dataLabels';

  const onFontColorChange = useCallback(
    (color: string) => {
      updateWidget({ [labelPropertyName]: { style: { color } } });
    },
    [labelPropertyName, updateWidget],
  );

  const onFontWeightChange = useCallback(
    (fontWeight: string) => {
      updateWidget({ [labelPropertyName]: { style: { fontWeight } } });
    },
    [labelPropertyName, updateWidget],
  );

  const onFontStyleChange = useCallback(
    (fontStyle: string) => {
      updateWidget({ [labelPropertyName]: { style: { fontStyle } } });
    },
    [labelPropertyName, updateWidget],
  );

  const fontColorProps = showFontColor ? { fontColor: showY2Datalabels ? y2Color : y1Color, onFontColorChange } : {};

  if (showY2Datalabels) {
    return (
      <TextSettings
        fontFamily={y2FontFamily}
        fontWeight={y2FontWeight}
        fontStyle={y2FontStyle}
        onFontWeightChange={onFontWeightChange}
        onFontStyleChange={onFontStyleChange}
        {...fontColorProps}
      />
    );
  }

  return (
    <TextSettings
      fontFamily={y1FontFamily}
      fontWeight={y1FontWeight}
      fontStyle={y1FontStyle}
      onFontWeightChange={onFontWeightChange}
      onFontStyleChange={onFontStyleChange}
      {...fontColorProps}
    />
  );
};
